/**
 * Sélecteur de période du tableau de bord et des rapports (FR-DSH-02).
 *
 * Les bornes sont des **dates civiles** de l'établissement (`IsoDate`,
 * `YYYY-MM-DD`), bornes incluses, conformément à `PeriodQuery`
 * (`packages/contracts/src/analytics.ts`). Le fuseau de StayAfrica
 * Ouagadougou est UTC+0 : les calculs se font en UTC, ce qui évite toute
 * dérive de jour côté navigateur.
 *
 * Hypothèse **A-049** (à confirmer par Bertrand) : « Ce mois », « Ce
 * trimestre » et « Cette année » s'arrêtent au jour courant (période à ce
 * jour) et non à la fin de la période ; « Mois dernier » couvre le mois
 * complet. Un graphique quotidien qui s'arrêterait à la fin du mois
 * afficherait des jours futurs vides et diluerait le taux d'occupation.
 */

export const PERIOD_PRESETS = [
  'TODAY',
  'LAST_7_DAYS',
  'THIS_MONTH',
  'LAST_MONTH',
  'THIS_QUARTER',
  'THIS_YEAR',
  'CUSTOM',
] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export interface PeriodRange {
  /** Date civile de début, incluse (`YYYY-MM-DD`). */
  from: string;
  /** Date civile de fin, incluse (`YYYY-MM-DD`). */
  to: string;
}

/** `2026-09-05` → millisecondes UTC du début de journée. */
function parse(date: string): number {
  const [year, month, day] = date.split('-').map(Number);
  return Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

function toIsoDate(millis: number): string {
  return new Date(millis).toISOString().slice(0, 10);
}

const DAY_MS = 86_400_000;

/** Décale une date civile de `days` jours. */
export function shiftDays(date: string, days: number): string {
  return toIsoDate(parse(date) + days * DAY_MS);
}

/** Nombre de jours d'une période, bornes incluses (au moins 1). */
export function daysInRange(range: PeriodRange): number {
  return Math.max(1, Math.round((parse(range.to) - parse(range.from)) / DAY_MS) + 1);
}

/** Date du jour dans le fuseau de l'établissement. */
export function todayIsoDate(timeZone = 'Africa/Ouagadougou', now = new Date()): string {
  // `en-CA` rend directement `YYYY-MM-DD`.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function endOfMonth(date: string): string {
  const [year, month] = date.split('-').map(Number);
  // Le jour 0 du mois suivant est le dernier jour du mois courant.
  return toIsoDate(Date.UTC(year ?? 1970, month ?? 1, 0));
}

/**
 * Bornes d'un préréglage. `custom` n'est utilisé que pour `CUSTOM` ; les
 * bornes y sont réordonnées si l'utilisateur saisit une fin avant le début.
 */
export function resolvePeriod(
  preset: PeriodPreset,
  today: string,
  custom?: Partial<PeriodRange>,
): PeriodRange {
  switch (preset) {
    case 'TODAY':
      return { from: today, to: today };
    case 'LAST_7_DAYS':
      return { from: shiftDays(today, -6), to: today };
    case 'THIS_MONTH':
      return { from: startOfMonth(today), to: today };
    case 'LAST_MONTH': {
      const lastMonthDay = shiftDays(startOfMonth(today), -1);
      return { from: startOfMonth(lastMonthDay), to: endOfMonth(lastMonthDay) };
    }
    case 'THIS_QUARTER': {
      const month = Number(today.slice(5, 7));
      const firstMonth = Math.floor((month - 1) / 3) * 3 + 1;
      return { from: `${today.slice(0, 4)}-${String(firstMonth).padStart(2, '0')}-01`, to: today };
    }
    case 'THIS_YEAR':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case 'CUSTOM': {
      const from = custom?.from || today;
      const to = custom?.to || today;
      return parse(from) <= parse(to) ? { from, to } : { from: to, to: from };
    }
  }
}

/** Période de comparaison : même durée, immédiatement avant. */
export function previousPeriod(range: PeriodRange): PeriodRange {
  const days = daysInRange(range);
  return { from: shiftDays(range.from, -days), to: shiftDays(range.from, -1) };
}

/** Liste des dates civiles d'une période, bornes incluses. */
export function eachDay(range: PeriodRange): string[] {
  const days: string[] = [];
  for (let cursor = parse(range.from); cursor <= parse(range.to); cursor += DAY_MS) {
    days.push(toIsoDate(cursor));
  }
  return days;
}
